import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { GetClassificationDetail } from '@/infrastructure/api-functions/interface/get-classification-detail.interface';
import type { ClassificationItem, ClassificationDetailItem } from '../type';
import { createPopper, type Instance, type Options } from '@popperjs/core';

const classificationPopperConfig:Partial<Options> = {
    placement: 'bottom',
    strategy: 'fixed',
    modifiers: [
        {
            name: 'offset',
            options: {
                offset: [0, 4],
            },
        },
        {
            name: 'preventOverflow',
            options: {
                boundary: 'viewport',
            },
        },
        {
            name: 'flip',
            options: {
              fallbackPlacements: ['top'],
            },
        },
    ],
};

export interface ClassificationPopoverService {
    isShowPopover: boolean;
    isLoading: boolean;
    classificationDetails: ClassificationDetailItem[];
    popperInstance: Instance | null;
    handleShowPopover: (item: ClassificationItem, target: HTMLElement, getReference: () => Promise<HTMLElement>) => void;
    handleHidePopover: () => void;
}

export const ClassificationPopoverService = Symbol('ClassificationPopoverService');

@injectable()
export class ClassificationPopoverServiceImpl implements ClassificationPopoverService {
    public isShowPopover: boolean = false;
    public isLoading: boolean = false;
    public classificationDetails:ClassificationDetailItem[] = [];
    public popperInstance: Instance | null = null;

    constructor(
        @inject(GetClassificationDetail)
        private _getClassificationDetailApi: GetClassificationDetail,
    ) {}

    private _createPopover(target: HTMLElement, reference: HTMLElement) {
        reference.style.zIndex = '99';
        this.popperInstance = createPopper(target, reference, classificationPopperConfig);
    }

    public async handleShowPopover(item: ClassificationItem, target: HTMLElement, getReference: () => Promise<HTMLElement>) {
        this.isLoading = true;
        this.isShowPopover = true;
        try {
            const reference = await getReference();
            this._createPopover(target, reference);
            this.classificationDetails = await this._getClassificationDetailApi.getClassificationDetail({
                type: 'ipc',
                name: item.code,
            })
            this.isLoading = false;
            this.popperInstance?.update();
        } catch (error) {
            this.isShowPopover = false;
            this.isLoading = false;
        }
    }

    public handleHidePopover() {
        this.isShowPopover = false;
        if (this.popperInstance) {
            this.popperInstance.destroy();
            this.popperInstance = null;
        }
    }
}
